import { useCallback, useEffect, useRef, useState } from 'react';

import { Bot, Download, Loader2, MessageSquarePlus, Send, Square, User, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { streamChatMessage } from '@/lib/api/chat.api';
import { generateDataPdf, type DataPdfPayload } from '@/lib/data-pdf';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chat.store';

import { extractFirstMetricKey, parseMetricChips } from './parseMetricChips';

import type { SSEEvent } from '@repo/shared-types';

export const ChatPanel = () => {
  const {
    currentChatId,
    messages,
    isStreaming,
    streamingMessage,
    thinkingContent,
    activeTools,
    completedTools,
    pendingReportPayload,
    focusMetric,
    metricLabels,
    startNewChat,
    addUserMessage,
    handleSSEEvent,
    finishStreaming,
    setCurrentChatId,
    setAbortController,
    cancelStreaming,
    closePanel,
  } = useChatStore();

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasAutoZoomedRef = useRef(false);

  useEffect(() => {
    // Instant scroll + near-bottom guard: 'smooth' re-animates on every streamed
    // token (flicker), and we shouldn't yank the view if the user scrolled up.
    const el = messagesEndRef.current;
    if (!el) return;
    const scroller = el.parentElement;
    if (scroller) {
      const nearBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 120;
      if (!nearBottom) return;
    }
    el.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingMessage, thinkingContent]);

  // Unconditional smooth scroll to the latest message — used on discrete actions
  // (sending a message, opening the panel), independent of the near-bottom guard.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ behavior }));
  }, []);

  // On open (the panel mounts only while open), jump to the latest messages.
  useEffect(() => {
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  const resizeTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    if (!streamingMessage || !focusMetric || hasAutoZoomedRef.current) return;
    const key = extractFirstMetricKey(streamingMessage);
    if (key) {
      hasAutoZoomedRef.current = true;
      focusMetric(key);
    }
  }, [streamingMessage, focusMetric]);

  useEffect(() => {
    if (isStreaming) hasAutoZoomedRef.current = false;
  }, [isStreaming]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    addUserMessage(trimmed);
    scrollToBottom('smooth');

    const controller = new AbortController();
    setAbortController(controller);

    try {
      await streamChatMessage(
        trimmed,
        currentChatId ?? undefined,
        (event: SSEEvent) => {
          handleSSEEvent(event);
          if (event.type === 'done') {
            finishStreaming(event.chat_id);
            if (!currentChatId) setCurrentChatId(event.chat_id);
          }
        },
        controller.signal,
      );
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        handleSSEEvent({
          type: 'error',
          message: (err as Error).message || 'Failed to send message',
        });
      }
    } finally {
      setAbortController(null);
    }
  }, [
    input,
    isStreaming,
    currentChatId,
    addUserMessage,
    handleSSEEvent,
    finishStreaming,
    setCurrentChatId,
    setAbortController,
    scrollToBottom,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleDownloadPDF = useCallback(() => {
    if (!pendingReportPayload) return;
    generateDataPdf(pendingReportPayload as DataPdfPayload);
  }, [pendingReportPayload]);

  const handleMetricClick = useCallback(
    (metricKey: string) => {
      focusMetric?.(metricKey);
    },
    [focusMetric],
  );

  return (
    <div className="bg-background flex h-full flex-col border-l">
      {/* ── Header ── */}
      <div className="bg-background/80 flex shrink-0 items-center justify-between border-b px-4 py-2.5 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/10">
            <Bot className="h-3.5 w-3.5 text-blue-500" />
          </div>
          <span className="text-sm font-semibold">Assistant</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="New chat"
            onClick={startNewChat}
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Close"
            onClick={closePanel}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="scrollbar-none flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.length === 0 && !isStreaming && (
          <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <Bot className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-foreground text-sm font-medium">How can I help?</p>
              <p className="mt-1 text-xs leading-relaxed">
                Ask about your data, or have me set up a campaign or workflow.
              </p>
            </div>
            <div className="mt-2 flex flex-wrap justify-center gap-1.5">
              {['Calls answered vs missed?', 'Car selected, no booking?', 'List campaigns'].map(
                (q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => {
                      setInput(q);
                      textareaRef.current?.focus();
                    }}
                    className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-full border px-3 py-1 text-[11px] transition-colors"
                  >
                    {q}
                  </button>
                ),
              )}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={cn('flex gap-2', msg.role === 'user' && 'flex-row-reverse')}>
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                msg.role === 'user' ? 'bg-primary' : 'bg-muted',
              )}
            >
              {msg.role === 'user' ? (
                <User className="text-primary-foreground h-3 w-3" />
              ) : (
                <Bot className="text-muted-foreground h-3 w-3" />
              )}
            </div>
            <div className="flex max-w-[85%] min-w-0 flex-col gap-1">
              <div
                className={cn(
                  'rounded-2xl px-3 py-2 text-[13px] leading-relaxed',
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-tr-sm'
                    : 'bg-muted text-foreground rounded-tl-sm',
                )}
              >
                {msg.role === 'assistant'
                  ? parseMetricChips(
                      msg.content,
                      metricLabels ?? undefined,
                      focusMetric ? handleMetricClick : undefined,
                    )
                  : msg.content}
              </div>

              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="text-muted-foreground flex items-center gap-1 px-1 text-[10px]">
                  <span className="opacity-60">
                    {msg.toolCalls.length} tool{msg.toolCalls.length > 1 ? 's' : ''} used
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Streaming state */}
        {isStreaming && (
          <div className="flex gap-2">
            <div className="bg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
              <Bot className="text-muted-foreground h-3 w-3" />
            </div>
            <div className="flex max-w-[85%] min-w-0 flex-col gap-1.5">
              {thinkingContent && !streamingMessage && (
                <div className="text-muted-foreground rounded-lg border px-2.5 py-1.5 text-[11px]">
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="italic">Thinking…</span>
                  </div>
                  <div className="max-h-20 overflow-y-auto whitespace-pre-wrap opacity-80">
                    {thinkingContent}
                  </div>
                </div>
              )}

              {activeTools.length > 0 && (
                <div className="space-y-1">
                  {activeTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="text-muted-foreground flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px]"
                    >
                      <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                      <span>
                        {tool.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {completedTools.length > 0 && !streamingMessage && (
                <div className="space-y-1">
                  {completedTools.map((tc, i) => (
                    <div
                      key={i}
                      className="text-muted-foreground flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px]"
                    >
                      <span className={tc.success ? 'text-green-500' : 'text-red-500'}>
                        {tc.success ? '✓' : '✗'}
                      </span>
                      <span>
                        {tc.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {streamingMessage && (
                <div className="bg-muted text-foreground rounded-2xl rounded-tl-sm px-3 py-2 text-[13px] leading-relaxed">
                  {parseMetricChips(
                    streamingMessage,
                    metricLabels ?? undefined,
                    focusMetric ? handleMetricClick : undefined,
                  )}
                  <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse rounded-full bg-blue-500" />
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Download PDF ── */}
      {!!pendingReportPayload && !isStreaming && (
        <div className="shrink-0 px-3 pb-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 rounded-xl"
            onClick={handleDownloadPDF}
          >
            <Download className="h-3.5 w-3.5" />
            Download PDF
          </Button>
        </div>
      )}

      {/* ── Input ── */}
      <div className="shrink-0 border-t px-3 py-2.5">
        <div className="bg-muted flex items-end gap-1.5 rounded-xl border px-3 py-1.5">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              resizeTextarea();
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything…"
            rows={1}
            className="text-foreground placeholder:text-muted-foreground flex-1 resize-none bg-transparent py-1 text-sm leading-snug outline-none"
          />
          {isStreaming ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              title="Stop"
              onClick={cancelStreaming}
            >
              <Square className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-blue-500 hover:text-blue-600"
              title="Send"
              onClick={handleSend}
              disabled={!input.trim()}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
